import { createUseStyles } from "react-jss";
import * as Mixins from "../../../theme/Mixins";
import {
    resolvePageMessageBorder,
    resolvePageMessageColor,
    resolvePageMessageFontSize,
} from "../../../theme/pageMessageThemeTokens";
import { resolveNonFieldsetGroupingInlineEdge } from "../../../theme/questionSpacingThemeTokens";
import { modernNpsCellBorderWidth } from "./NetPromoterItemRenderer.styles";
import { Theme } from "../../../types/Theme";

export default createUseStyles((theme: Theme) => {
    const inlineEdge = resolveNonFieldsetGroupingInlineEdge(theme);

    return {
        page: {
            composes: "page",
            height: "100%",
            color: () => Mixins.foregroundColor(theme, Mixins.wrapperBackgroundColor(theme)),
            transition: "opacity 2000ms",

            "& .separator": {
                height: 0,
                border: 0,
                margin: [[theme.PageSeparatorsSpacing, 0]],
                borderTop: () =>
                    `${theme.PageSeparatorsWidth}px solid ${Mixins.foregroundColor(
                        theme,
                        Mixins.wrapperBackgroundColor(theme),
                    )}`,
            },

            "& img, video": {
                maxWidth: "100%",
            },

            "& fieldset": {
                display: "block",
                border: 0,
                margin: 0,
                padding: 0,
                "& legend": {
                    margin: 0,
                    padding: 0,
                },
            },

            "& .non-fieldset-grouping-question": {
                display: "block",
                border: 0,
                margin: 0,
                padding: 0,
                "margin-inline-start": inlineEdge,
                "margin-inline-end": inlineEdge,
                "padding-block-start": "0.35em",
                "padding-block-end": "0.625em",

                "& div.question__legend": {
                    margin: 0,
                    padding: 0,
                    display: "block",
                    "padding-inline-start": inlineEdge,
                    "padding-inline-end": inlineEdge,
                    "border-width": "initial",
                    "border-style": "none",
                    "border-color": "initial",
                    "border-image": "initial",
                },
            },
        },
        pageWrapper: {
            composes: "page__wrapper",
            minHeight: "100%",
            margin: [[0, "auto"]],
            padding: () => (theme.TopNavigationPosition === "floating" ? [[61, 109]] : [[56, 109]]),
            position: "relative",
            background: theme.PageWrapperBackground,
            boxSizing: "border-box",
            maxWidth: theme.PageWrapperMaxWidth,
        },
        dynamicPageWrapper: {
            composes: "page__wrapper--dynamic",
            padding: () => {
                return "16px 29px 29px 29px";
            }, // It must be a function to override pageWrapper padding!
        },
        pageContent: {
            composes: "page__content",
            paddingTop: 29,
        },
        pageMessage: {
            composes: "page__message",
            textAlign: "center",
            margin: "106px 0",
            padding: 29,
            // Theme is fixed for this stylesheet instance (createUseStyles closure).
            border: resolvePageMessageBorder(theme),
            borderRadius: theme.BorderRadius,
            fontSize: resolvePageMessageFontSize(theme),
            color: resolvePageMessageColor(theme),
        },
        pageHeader: {
            composes: "page__header",
            marginBottom: () => Mixins.getSpacing(theme, 58, 0),
            "& h1, h2, h3, h4, h5, h6": {
                margin: 0,
                padding: 0,
            },
        },
        pageHeaderLogo: {
            composes: "header__logo",
            display: "block",
            maxWidth: 250,
            maxHeight: 250,
            marginLeft: () => (theme.LogoAlign === "left" ? undefined : "auto"),
            marginRight: () => (theme.LogoAlign === "right" ? undefined : "auto"),
        },
        pageHeaderContent: {
            composes: "header__content",
            marginTop: () => Mixins.getSpacing(theme, 20, theme.QuestionSpacing?.WithinComponents),
            outline: "none",
        },
        pageFooter: {
            composes: "page__footer",
            "& h1, h2, h3, h4, h5, h6": {
                margin: 0,
                padding: 0,
            },
        },
        backlinkContainer: {
            composes: "backlink-container",
            position: "sticky",
            bottom: 0,
            float: "right",
            padding: ["10px", "30px"],
            borderRadius: ["5px", "0", "0", "0"],
            backgroundColor: () => Mixins.alpha(theme.PrimaryColor, 0.4),
            color: () => Mixins.foregroundColor(theme, theme.PrimaryColor),
            "&:hover": {
                backgroundColor: theme.PrimaryColor,
            },
        },
        backlinkContainerMobilePreview: {
            composes: "backlink-container backlink-container--mobile-preview",
            position: "relative",
            bottom: 0,
            right: "-5px",
            padding: ["10px", "16px"],
            borderRadius: ["5px", "0", "0", "0"],
            backgroundColor: () => Mixins.alpha(theme.PrimaryColor, 0.9),
            color: () => Mixins.foregroundColor(theme, theme.PrimaryColor),
            zIndex: 10,
            "&:hover": {
                backgroundColor: theme.PrimaryColor,
            },
        },
        backlink: {
            composes: "backlink",
            color: () => Mixins.foregroundColor(theme, theme.PrimaryColor),
        },
        /** New theme: full-width centered footer line (normal flow; rendered last in page). */
        backlinkNewOuter: {
            width: "100%",
            display: "flex",
            justifyContent: "center",
            paddingTop: 16,
            boxSizing: "border-box",
        },
        backlinkNewText: {
            margin: 0,
            fontSize: 12,
            fontWeight: 400,
            letterSpacing: "-0.025em",
            color: () => Mixins.foregroundColor(theme, Mixins.wrapperBackgroundColor(theme)),
            opacity: 0.72,
            textAlign: "center",
        },
        backlinkNewLink: {
            color: () => Mixins.foregroundColor(theme, Mixins.wrapperBackgroundColor(theme)),
            textDecoration: "underline",
            fontWeight: 600,
            transition: "color 150ms ease, text-decoration-color 150ms ease",
            "&:hover": {
                color: theme.PrimaryColor,
                textDecorationColor: theme.PrimaryColor,
            },
        },
        skiplinkContainer: {
            // https://webaim.org/techniques/css/invisiblecontent/#skipnavlinks
            composes: "skiplinkContainer",
            "& a": {
                position: "absolute",
                left: "-10000px",
                top: "auto",
                width: "1px",
                height: "1px",
                overflow: "hidden",
                "&:focus": {
                    position: "static",
                    width: "auto",
                    height: "auto",
                    ...(theme.UseModernTheme === true
                        ? {
                              display: "inline-block",
                              overflow: "visible",
                              boxSizing: "border-box",
                              outline: "none",
                              borderRadius: theme.BorderRadius,
                              border: `${modernNpsCellBorderWidth(theme)}px solid ${theme.PrimaryColor}`,
                              padding: [[2, 2]],
                          }
                        : {}),
                },
            },
        },
    } as any;
});
